<template>
    <div class="error-msg-wrap">
        <div class="form-wrapper slide-up" style="width:560px">
            <div class="form-close">
                <button type="button" class="form-close-x" title="Close" @click="$emit('close')">&times;</button>
            </div>
            <h2 class="sb-modal-title">{{ metric ? 'Update Metric' : 'Add Metric' }}</h2>

            <div class="sb-label">Metric</div>
            <input type="text" v-model="form.Name" placeholder="e.g. Quotes sent" ref="nameInput" />

            <div class="sb-row">
                <div>
                    <div class="sb-label">Owner</div>
                    <select v-model="form.OwnerEmployeeID">
                        <option :value="null">No owner</option>
                        <optgroup v-if="teamMembers.length" :label="teamLabel(form.Team)">
                            <option v-for="e in teamMembers" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                        </optgroup>
                        <optgroup :label="teamMembers.length ? 'Everyone else' : 'Everyone'">
                            <option v-for="e in otherEmployees" :key="e.ID" :value="e.ID">{{ e.Name }}</option>
                        </optgroup>
                    </select>
                </div>
                <div v-if="teams.length > 1">
                    <div class="sb-label">Team</div>
                    <select v-model="form.Team">
                        <option v-for="t in teams" :key="t" :value="t">{{ teamLabel(t) }}</option>
                    </select>
                </div>
            </div>

            <div class="sb-label">Goal</div>
            <div class="sb-goalrow">
                <select v-model="form.Cmp" class="sb-goal-cmp">
                    <option value="gte">At least</option>
                    <option value="lte">At most</option>
                    <option value="eq">Exactly</option>
                    <option value="range">Between</option>
                </select>
                <input type="number" step="any" v-model="form.Goal" aria-label="Goal" />
                <template v-if="form.Cmp == 'range'">
                    <span>and</span>
                    <input type="number" step="any" v-model="form.GoalMax" aria-label="Upper goal" />
                </template>
                <input type="text" v-model="form.Unit" placeholder="unit" aria-label="Unit" class="sb-goal-unit" />
            </div>
            <div class="sb-hint">Unit is optional: $, $K, % or a word like min or days.</div>

            <div v-if="metric && goalChanged" class="sb-when">
                <label><input type="radio" value="now" v-model="form.GoalWhen" /> New goal starts this week <span class="sb-hint">(past weeks keep their old goal)</span></label>
                <label><input type="radio" value="all" v-model="form.GoalWhen" /> The goal was wrong, fix every week</label>
            </div>
            <div v-if="metric && metric.Goals.length > 1" class="sb-history">
                <b>Goal history</b>
                <span v-for="(g, i) in metric.Goals" :key="g.FromWeek">{{ i == 0 ? 'First' : 'From ' + weekLabel(g.FromWeek) }}: {{ goalTextOf(g) }}</span>
            </div>

            <div class="sb-label">How it's counted</div>
            <textarea v-model="form.Definition" rows="2" placeholder="e.g. Quotes sent to dealers or customers, counted once per quote number."></textarea>
            <div class="sb-hint">If two people could count it differently, the definition isn't done yet.</div>

            <div class="sb-actions">
                <a v-if="metric" class="sb-remove" @click.prevent="showConfirmDelete = true">Remove Metric</a>
                <span style="flex:1"></span>
                <div class="btn-light" @click="$emit('close')">Cancel</div>
                <div class="btn" :class="saving ? 'disable-button' : ''" @click="save()">{{ metric ? 'Save' : 'Add' }}</div>
            </div>
        </div>

        <confirm-delete v-if="showConfirmDelete" @confirm="remove()" @cancel="showConfirmDelete = false" msg="Remove this metric and all of its numbers?" />
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import ConfirmDelete from './ConfirmDelete.vue';
import { goalAt, weekLabel, formatValue } from '@/scoreboard';

export default defineComponent({
    name: 'ScoreboardMetricModal',
    // metric: the one being edited, or null to add one to team. week: this week's Monday.
    props: [ 'metric', 'team', 'teams', 'employees', 'week' ],
    emits: [ 'close', 'saved' ],
    components: {
        ConfirmDelete
    },
    data() {
        return {
            form: {} as any,
            saving: false,
            showConfirmDelete: false
        }
    },
    created() {
        const m = this.metric;
        const g = m ? goalAt(m, this.week) : null;
        this.form = {
            Name: m ? m.Name : '',
            OwnerEmployeeID: m ? m.OwnerEmployeeID : (this.$store.state.user.employeeID || null),
            Team: m ? m.Team : this.team,
            Cmp: g ? g.Cmp : 'gte',
            Goal: g ? g.Goal : '',
            GoalMax: g && g.GoalMax !== null ? g.GoalMax : '',
            Unit: m ? m.Unit : '',
            Definition: m ? m.Definition : '',
            GoalWhen: 'now'
        };
    },
    mounted() {
        (this.$refs.nameInput as HTMLInputElement)?.focus();
    },
    computed: {
        teamMembers(): any[] {
            return (this.employees || []).filter((e: any) => (e.Teams || []).includes(this.form.Team));
        },
        otherEmployees(): any[] {
            return (this.employees || []).filter((e: any) => !(e.Teams || []).includes(this.form.Team));
        },
        // The goal in effect this week differs from what's in the form
        goalChanged(): boolean {
            const g = goalAt(this.metric, this.week);
            if(!g) {
                return true;
            }
            return g.Cmp != this.form.Cmp || Number(g.Goal) != Number(this.form.Goal)
                || (this.form.Cmp == 'range' && Number(g.GoalMax) != Number(this.form.GoalMax));
        }
    },
    methods: {
        weekLabel,
        goalTextOf(g: any): string {
            const m = { Unit: this.form.Unit };
            if(g.Cmp == 'range') {
                return `${formatValue(m, g.Goal)}–${formatValue(m, g.GoalMax)}`;
            }
            return (g.Cmp == 'gte' ? '≥ ' : g.Cmp == 'lte' ? '≤ ' : '= ') + formatValue(m, g.Goal);
        },
        async save() {
            if(this.saving) {
                return;
            }
            if(!this.form.Name.trim() || this.form.Goal === '' || (this.form.Cmp == 'range' && this.form.GoalMax === '')) {
                alert(this.form.Cmp == 'range' ? 'Give it a name and both ends of the range.' : 'Give it a name and a goal number.');
                return;
            }
            this.saving = true;
            await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/metric`, {
                ...this.form,
                ID: this.metric ? this.metric.ID : null,
                Week: this.week
            })
                .then(() => {
                    this.$emit('saved', this.form.Team);
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error');
                })
            this.saving = false;
        },
        async remove() {
            this.showConfirmDelete = false;
            await axios.post(`${this.$store.state.apiUrl}/api/scoreboard/metric/delete`, { ID: this.metric.ID })
                .then(() => {
                    this.$emit('saved', this.metric.Team);
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error');
                })
        }
    }
});
</script>

<style scoped>
.sb-modal-title { margin: 0 0 6px; font-size: 20px; }
.sb-label { margin: 18px 0 8px; font-size: 14px; font-weight: bold; }
.sb-hint { font-weight: normal; color: var(--font-mid-gray); font-size: 12px; margin-top: 4px; }
.sb-row { display: flex; gap: 15px; }
.sb-row > div { flex: 1; min-width: 0; }
.sb-row select { width: 100%; }
.sb-goalrow { display: flex; align-items: center; gap: 8px; }
.sb-goalrow input, .sb-goalrow select { margin: 0; min-width: 0; }
.sb-goalrow input[type=number] { flex: 1; }
.sb-goal-cmp { flex: 0 0 120px; }
.sb-goal-unit { flex: 0 0 80px; }
.sb-when { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; font-size: 13.5px; }
.sb-when label { display: flex; align-items: baseline; gap: 6px; cursor: pointer; }
.sb-history { display: flex; flex-direction: column; gap: 2px; margin-top: 12px; padding: 10px 12px; background: var(--light-gray); border-radius: 8px; font-size: 12.5px; }
.sb-actions { display: flex; align-items: center; gap: 10px; margin-top: 24px; }
.sb-actions .btn, .sb-actions .btn-light { margin: 0; padding: 8px 18px; }
.sb-remove { color: var(--primary-color); text-decoration: underline; cursor: pointer; font-size: 13.5px; }
</style>
